import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { logger } from '@core/utils';
import config from '@core/config';
import type { ApiError } from '@core/types/api';

/**
 * localStorage keys the persisted session lives under. They MIRROR the feature
 * slices (`@features/auth/store/authSlice` `AUTH_STORAGE_KEY` and
 * `@features/organization/store/organizationSlice` `ACTIVE_ORGANIZATION_KEY`)
 * and are duplicated on purpose: core must never import from a feature.
 */
export const AUTH_STORAGE_KEY = 'afterbooking.auth';
export const ACTIVE_ORGANIZATION_STORAGE_KEY = 'activeOrganizationUuid';

interface AuthSliceShape {
  auth?: { token?: string | null };
}

interface OrganizationSliceShape {
  organization?: { activeOrganizationUuid?: string | null };
}

/**
 * Read the token out of the persisted session, best effort.
 *
 * Only a fallback: the auth slice rehydrates itself at module load, so Redux
 * state is normally authoritative by the time any request goes out.
 */
function persistedToken(): string | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { token?: string } | null;
    return parsed?.token ?? null;
  } catch {
    return null;
  }
}

/**
 * Populate request headers from Redux state, falling back to localStorage.
 *
 * Exported so it can be unit-tested deterministically without the network:
 * the active organization (the paying tenant) is sent as the `X-Organization`
 * header so the backend can scope every query to it.
 */
export function prepareHeaders(headers: Headers, getState: () => unknown): Headers {
  const state = getState() as AuthSliceShape & OrganizationSliceShape;

  // Try Redux state first, fallback to the persisted session.
  const token = state.auth?.token || persistedToken();
  if (token) {
    // Our ExpiringTokenAuthentication expects "Token <token>", not "Bearer".
    headers.set('Authorization', `Token ${token}`);
  }

  // Active organization (tenant) selection — state first, localStorage fallback.
  const activeOrganization =
    state.organization?.activeOrganizationUuid ||
    localStorage.getItem(ACTIVE_ORGANIZATION_STORAGE_KEY);
  if (activeOrganization) {
    headers.set('X-Organization', activeOrganization);
  }

  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  return headers;
}

/**
 * Base query with automatic token + active-organization injection.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: config.apiBaseUrl,
  prepareHeaders: (headers, { getState }) => prepareHeaders(headers, getState),
});

interface RawDataError {
  detail?: string;
  message?: string;
  error?: string;
  non_field_errors?: string | string[];
}

/**
 * Transform error responses to a consistent shape.
 *
 * The normalized `error` field carries an **i18n key** (`errors.*`) for the
 * cases the SPA owns, or the server's own (already localized) message when DRF
 * sent one. Render it through `t()`: i18next echoes unknown keys verbatim, so a
 * server message passes through untouched while our own copy stays translatable.
 */
function transformError(error: FetchBaseQueryError): ApiError {
  // Network / parsing failures surface a string `error` field.
  if ('error' in error) {
    return {
      status: 'FETCH_ERROR',
      error: 'errors.network',
      originalError: error.error,
    };
  }

  if ('status' in error) {
    const status = error.status;
    const data = error.data as RawDataError | string | undefined;

    let message = 'errors.generic';

    if (typeof data === 'string') {
      message = data;
    } else if (data?.detail) {
      message = data.detail;
    } else if (data?.message) {
      message = data.message;
    } else if (data?.error) {
      message = data.error;
    } else if (data?.non_field_errors) {
      message = Array.isArray(data.non_field_errors)
        ? data.non_field_errors.join(', ')
        : data.non_field_errors;
    }

    switch (status) {
      case 401:
        message = 'errors.unauthorized';
        break;
      case 403:
        message = 'errors.forbidden';
        break;
      case 404:
        message = 'errors.notFound';
        break;
      case 429:
        message = 'errors.tooManyRequests';
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        message = 'errors.server';
        break;
    }

    return {
      status,
      error: message,
      data,
      originalError: error,
    };
  }

  return {
    status: 'UNKNOWN_ERROR',
    error: 'errors.generic',
    originalError: error,
  };
}

type ExtendedArgs = (FetchArgs & { formData?: boolean }) | string;

/**
 * Enhanced base query with error transformation and FormData support.
 */
export const baseQuery: BaseQueryFn<ExtendedArgs, unknown, ApiError> = async (
  args,
  api,
  extraOptions,
) => {
  let queryArgs = args;

  // For FormData requests, strip the formData flag so fetchBaseQuery receives a
  // normal args object. The browser auto-sets the correct Content-Type with the
  // multipart boundary when the body is FormData.
  if (typeof queryArgs === 'object' && queryArgs.formData) {
    queryArgs = { ...queryArgs };
    delete queryArgs.formData;
  }

  const result = await rawBaseQuery(queryArgs, api, extraOptions);

  if (result.error) {
    if (import.meta.env.DEV) {
      logger.error('API Error:', {
        endpoint: typeof queryArgs === 'string' ? queryArgs : queryArgs.url,
        error: result.error,
      });
    }

    const state = api.getState() as AuthSliceShape & OrganizationSliceShape;

    // A 401 while authenticated means the token expired (4h sliding inactivity)
    // or was rotated by a newer login elsewhere — drop the session so the SPA
    // redirects to /login. (Skip when not authenticated, e.g. a failed login.)
    // The `expired` payload is what lets the login screen explain the redirect
    // rather than looking like it dropped the user for no reason.
    if (result.error.status === 401 && state.auth?.token) {
      api.dispatch({ type: 'auth/logout', payload: { expired: true } });
    }

    // A 403 while authenticated with an active organization usually means that
    // uuid is stale for this user (e.g. membership revoked, the tenant suspended,
    // or a forged header the backend refused). Reject it so the SPA re-selects a
    // DIFFERENT one — `rejectActiveOrganization` also records the uuid, which is
    // what stops `useActiveOrganization` from re-selecting it from the persisted
    // login payload on the next render (403 → clear → re-select → 403 → …). Never
    // log the user out here; only 401 does that.
    if (
      result.error.status === 403 &&
      state.auth?.token &&
      state.organization?.activeOrganizationUuid
    ) {
      api.dispatch({ type: 'organization/rejectActiveOrganization' });
      try {
        localStorage.removeItem(ACTIVE_ORGANIZATION_STORAGE_KEY);
      } catch {
        /* ignore — best effort */
      }
    }

    return { ...result, error: transformError(result.error) };
  }

  return result as { data: unknown };
};

/**
 * Base query for public endpoints (no authentication).
 */
export const publicBaseQuery = fetchBaseQuery({
  baseUrl: config.apiBaseUrl,
  prepareHeaders: (headers) => {
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    return headers;
  },
});
